import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {PortalSettingsGetSchema} from '../../../../api/DBSchema/PortalSettings';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import {TimezoneValues} from '../../../../api/DBSchema/Timezone';
import Indentation from '../../../../components/Simple/Indentation';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCButton from '../../../../components/Simple/SMCButton';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import {useBoolean} from '../../../../utils/hooks';
import ExportDomainSMPModal from '../../../PopupPages/ExportDomainSMPModal';
import ImportDomainSMPModal from '../../../PopupPages/ImportDomainSMPModal';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import Sepperator from '../../../../components/Simple/Sepperator';

const pageSchema = PortalSettingsGetSchema.pages.general;
const pageId = pageSchema.pageId;

function PortalSettingsGeneral({
    portalSettingsObject,
    setPortalSettingsData
}) {
    const { t } = useTranslation();

    const pageData = portalSettingsObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setPortalSettingsData([pageId, paramName], value);
    }, [setPortalSettingsData]);

    const smpName                 = pageData[pageSchema.smpName];
    const isServiceDomainTimeZone = pageData[pageSchema.isServiceDomainTimeZone];
    const serviceDomainTimeZone   = pageData[pageSchema.serviceDomainTimeZone];
    const [isExportDomainModalOpen, openExportDomainModal, closeExportDomainModal] = useBoolean(false);
    const [isImportDomainModalOpen, openImportDomainModal, closeImportDomainModal] = useBoolean(false);

    const timezoneOptions = useMemo(() => (
        TimezoneValues.map(timezoneConf => (
                <SMCOption key={timezoneConf['location']} value={timezoneConf['location']}>
                    {t(timezoneConf['i18n'])}
                </SMCOption>
            )
        )
    ), [t]);

    return (
        <EditorLayout>
            <SMCTextInput
                value={smpName}
                readOnly={true}
                onChange={e => setPageValue(pageSchema.smpName, e.target.value)}
                label={t('SMP.SETTINGS.GENERAL.SERVICE_CENTER_NAME')}
                tooltip={t('SMP.SETTINGS.GENERAL.SERVICE_CENTER_NAME.TOOLTIP')}
            />
            <SMCCheckbox
                label={t('SMP.SETTINGS.GENERAL.SET_TIME_ZONE')}
                tooltip={t('SMP.SETTINGS.GENERAL.SET_TIME_ZONE.TOOLTIP')}
                checked={isServiceDomainTimeZone}
                onChange={e => setPageValue(pageSchema.isServiceDomainTimeZone, e.target.checked)}
            />
            <Indentation>
                <SMCSelect
                    value={serviceDomainTimeZone}
                    onChange={e => setPageValue(pageSchema.serviceDomainTimeZone, e.target.value)}
                    label={t('SMP.SETTINGS.GENERAL.TIME_ZONE')}
                    disabled={!isServiceDomainTimeZone}
                    showEmptyOptionOnDisabled={true}
                >
                    { timezoneOptions }
                </SMCSelect>
            </Indentation>
            <EmptyRow />

            <Sepperator />

            <SMCLabel bold>Export / Import service domain</SMCLabel>
            <RowLayout height={40}>
                <SMCLabel width={320}>Export service domain</SMCLabel>
                <SMCButton onClick={openExportDomainModal}>{t('SMC_SYSOPS_EXPORT')}</SMCButton>
            </RowLayout>
            <RowLayout height={40}>
                <SMCLabel width={320}>Import service domain</SMCLabel>
                <SMCButton onClick={openImportDomainModal}>{t('SMC_SYSOPS_IMPORT')}</SMCButton>
            </RowLayout>

            {/* Popup pages */}
            <ExportDomainSMPModal
                isOpen={isExportDomainModalOpen}
                onRequestClose={closeExportDomainModal}
            />
            <ImportDomainSMPModal
                isOpen={isImportDomainModalOpen}
                onRequestClose={closeImportDomainModal}
            />
            <EmptyRow />
        </EditorLayout>
    )
}

export default PortalSettingsGeneral;